import * as React from "react";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import { useQuery } from "@tanstack/react-query";
import Typography from "@mui/material/Typography";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import CircularProgress from "@mui/material/CircularProgress";
import { VariantSignificanceIcon } from "./VariantSignificanceIcon";
import FilterToolbar from "./VariantFilterToolbar";
import VariantRow from "./ExpandableVariantRow";
import getVariants from "../../../apis/getVariants";
import { romanToInt } from "../../../utils/utils";
import preLodaer from "../../../assets/images/preloader.gif";
import HPOPrioritization from "./HPOPrioritization";
import {
  checkPrioritizationStatus,
  prioritizeVariantsByHPO,
} from "../../../apis";
import { toast } from "react-toastify";

// Move columns outside component to prevent recreation
const columns = [
  {
    field: "intervar_classification",
    headerName: "ACMG Classification",
    flex: 0.4,
    renderCell: (params) => {
      return <VariantSignificanceIcon classification={params.value} />;
    },
  },
  {
    field: "cancervar_classification",
    headerName: "AMP Classification",
    flex: 0.4,
    renderCell: (params) => {
      return <VariantSignificanceIcon classification={params.value} />;
    },
    sortComparator: (v1, v2, cellParams1, cellParams2) => {
      return (
        romanToInt(v1.split("#")[1].split("_")[0]) -
        romanToInt(v2.split("#")[1].split("_")[0])
      );
    },
  },
  { field: "gene_symbol", headerName: "Gene", flex: 0.2 },
  { field: "location", headerName: "Position", flex: 0.3 },
  { field: "function", headerName: "Function", flex: 0.2 },
  {
    field: "clinvar_classification",
    headerName: "Clinvar Significance",
    flex: 0.4,
  },
];

// Move NoRowsOverlay outside and memoize
const NoRowsOverlay = React.memo(function NoRowsOverlay() {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
      }}
    >
      <SearchOffIcon sx={{ fontSize: 60, color: "text.secondary", mb: 1 }} />
      <Typography variant="h6" color="text.secondary">
        No variants found
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Try adjusting your filters or search criteria
      </Typography>
    </Box>
  );
});

// Create a null header component outside
const NullHeader = () => null;

// Move filter base generation outside component
const getFilterModelBase = (projectType) => {
  const baseFilters = [
    {
      label: "Consequence",
      type: "select",
      filters: [
        {
          label: "3' UTR Variant",
          value: "3_prime_UTR_variant",
          active: false,
        },
        {
          label: "5' UTR Variant",
          value: "5_prime_UTR_variant",
          active: false,
        },
        {
          label: "Coding Sequence Variant",
          value: "coding_sequence_variant",
          active: false,
        },
        {
          label: "Downstream Gene Variant",
          value: "downstream_gene_variant",
          active: false,
        },
        {
          label: "Frameshift Variant",
          value: "frameshift_variant",
          active: false,
        },
        {
          label: "Incomplete Terminal Codon Variant",
          value: "incomplete_terminal_codon_variant",
          active: false,
        },
        { label: "Inframe Deletion", value: "inframe_deletion", active: false },
        {
          label: "Inframe Insertion",
          value: "inframe_insertion",
          active: false,
        },
        {
          label: "Intergenic Variant",
          value: "intergenic_variant",
          active: false,
        },
        { label: "Intron Variant", value: "intron_variant", active: false },
        {
          label: "Mature miRNA Variant",
          value: "mature_miRNA_variant",
          active: false,
        },
        { label: "Missense Variant", value: "missense_variant", active: false },
        {
          label: "Non-coding Transcript Exon Variant",
          value: "non_coding_transcript_exon_variant",
          active: false,
        },
        {
          label: "Protein Altering Variant",
          value: "protein_altering_variant",
          active: false,
        },
        {
          label: "Regulatory Region Ablation",
          value: "regulatory_region_ablation",
          active: false,
        },
        {
          label: "Regulatory Region Variant",
          value: "regulatory_region_variant",
          active: false,
        },
        {
          label: "Splice Acceptor Variant",
          value: "splice_acceptor_variant",
          active: false,
        },
        {
          label: "Splice Donor 5th Base Variant",
          value: "splice_donor_5th_base_variant",
          active: false,
        },
        {
          label: "Splice Donor Region Variant",
          value: "splice_donor_region_variant",
          active: false,
        },
        {
          label: "Splice Donor Variant",
          value: "splice_donor_variant",
          active: false,
        },
        {
          label: "Splice Polypyrimidine Tract Variant",
          value: "splice_polypyrimidine_tract_variant",
          active: false,
        },
        {
          label: "Splice Region Variant",
          value: "splice_region_variant",
          active: false,
        },
        { label: "Start Lost", value: "start_lost", active: false },
        { label: "Stop Gained", value: "stop_gained", active: false },
        { label: "Stop Lost", value: "stop_lost", active: false },
        {
          label: "Stop Retained Variant",
          value: "stop_retained_variant",
          active: false,
        },
        {
          label: "Synonymous Variant",
          value: "synonymous_variant",
          active: false,
        },
        {
          label: "Transcript Ablation",
          value: "transcript_ablation",
          active: false,
        },
        {
          label: "Upstream Gene Variant",
          value: "upstream_gene_variant",
          active: false,
        },
      ],
    },
    {
      label: "Gene_Symbol",
      type: "input",
      filters: [{ label: "Symbol", value: "", active: false }],
    },
    {
      label: "rsID",
      type: "input",
      filters: [{ label: "rsID", value: "", active: false }],
    },
    {
      label: "gnomAD_AF",
      type: "button",
      filters: [
        { label: "< 0.001", value: "0.001", active: false, exclude: false },
        { label: "< 0.01", value: "0.01", active: false, exclude: false },
        { label: "< 0.05", value: "0.05", active: false, exclude: false },
        { label: "< 0.1", value: "0.1", active: false, exclude: false },
      ],
    },
    {
      label: "Zygosity",
      type: "button",
      filters: [
        { label: "HOM", value: "HOM", active: false, exclude: false },
        { label: "HET", value: "HET", active: false, exclude: false },
      ],
    },
    {
      label: "Father_Zygosity",
      type: "button",
      filters: [
        { label: "HOM", value: "HOM", active: false, exclude: false },
        { label: "HET", value: "HET", active: false, exclude: false },
      ],
    },
    {
      label: "Mother_Zygosity",
      type: "button",
      filters: [
        { label: "HOM", value: "HOM", active: false, exclude: false },
        { label: "HET", value: "HET", active: false, exclude: false },
      ],
    },
  ];

  if (projectType === "SOMATIC") {
    return [
      {
        label: "AMP",
        type: "button",
        filters: [
          {
            label: "Tier I - Pathogenic",
            value: "pathogenic",
            active: false,
            exclude: false,
          },
          {
            label: "Tier II - Potential",
            value: "potential",
            active: false,
            exclude: false,
          },
          {
            label: "Tier III - VUS",
            value: "uncertain_significance",
            active: false,
            exclude: false,
          },
          {
            label: "Tier IV - Benign",
            value: "benign",
            active: false,
            exclude: false,
          },
          {
            label: "Tier IV - Likely Benign",
            value: "likely_benign",
            active: false,
            exclude: false,
          },
        ],
      },
      {
        label: "ClinVar",
        type: "button",
        filters: [
          {
            label: "P",
            fullLabel: "Pathogenic",
            value: "pathogenic",
            active: false,
            exclude: false,
          },
          {
            label: "LP",
            fullLabel: "Likely Pathogenic",
            value: "likely_pathogenic",
            active: false,
            exclude: false,
          },
          {
            label: "VUS",
            fullLabel: "Variant of Uncertain Significance",
            value: "vus",
            active: false,
            exclude: false,
          },
          {
            label: "LB",
            fullLabel: "Likely Benign",
            value: "likely_benign",
            active: false,
            exclude: false,
          },
          {
            label: "B",
            fullLabel: "Benign",
            value: "benign",
            active: false,
            exclude: false,
          },
        ],
      },
      ...baseFilters,
    ];
  } else {
    return [
      {
        label: "ACMG",
        type: "button",
        filters: [
          {
            label: "P",
            fullLabel: "Pathogenic",
            value: "pathogenic",
            active: false,
            exclude: false,
          },
          {
            label: "LP",
            fullLabel: "Likely Pathogenic",
            value: "likely_pathogenic",
            active: false,
            exclude: false,
          },
          {
            label: "VUS",
            fullLabel: "Variant of Uncertain Significance",
            value: "uncertain_significance",
            active: false,
            exclude: false,
          },
          {
            label: "LB",
            fullLabel: "Likely Benign",
            value: "likely_benign",
            active: false,
            exclude: false,
          },
          {
            label: "B",
            fullLabel: "Benign",
            value: "benign",
            active: false,
            exclude: false,
          },
        ],
      },
      {
        label: "ClinVar",
        type: "button",
        filters: [
          {
            label: "P",
            fullLabel: "Pathogenic",
            value: "pathogenic",
            active: false,
            exclude: false,
          },
          {
            label: "LP",
            fullLabel: "Likely Pathogenic",
            value: "likely_pathogenic",
            active: false,
            exclude: false,
          },
          {
            label: "VUS",
            fullLabel: "Variant of Uncertain Significance",
            value: "vus",
            active: false,
            exclude: false,
          },
          {
            label: "LB",
            fullLabel: "Likely Benign",
            value: "likely_benign",
            active: false,
            exclude: false,
          },
          {
            label: "B",
            fullLabel: "Benign",
            value: "benign",
            active: false,
            exclude: false,
          },
        ],
      },
      ...baseFilters,
    ];
  }
};

function VariantList(props) {
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);

  // Memoize initial filter model based on project_type
  const initialFilterModel = React.useMemo(
    () => getFilterModelBase(props.project_type),
    [props.project_type]
  );

  const [filterModel, setFilterModel] = React.useState(initialFilterModel);
  const [hasInitialData, setHasInitialData] = React.useState(false);
  const [isPrioritizing, setIsPrioritizing] = React.useState(false);
  const [selectedHPOTerms, setSelectedHPOTerms] = React.useState([]);
  const apiRef = useGridApiRef();

  const { isLoading, isFetching, isError, data, error, refetch } = useQuery({
    queryKey: ["variants", page, pageSize, filterModel],
    queryFn: () =>
      getVariants(props.project_id, page + 1, pageSize, filterModel),
    placeholderData: (previousData) => previousData,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const isPrioritized = data?.is_prioritized || false;
  const sampleId = data?.sample_id;

  // Only poll when actively prioritizing
  const { data: statusData } = useQuery({
    queryKey: ["prioritization-status", sampleId],
    queryFn: () => checkPrioritizationStatus(sampleId),
    enabled: isPrioritizing && !!sampleId, // Only run when prioritizing
    refetchInterval: isPrioritizing ? 60000 : false, // Only poll when prioritizing
    refetchIntervalInBackground: false,
  });

  React.useEffect(() => {
    if (statusData?.data) {
      if (statusData.data.hpo_terms) {
        setSelectedHPOTerms(statusData.data.hpo_terms);
      }
      if (statusData.data.is_prioritized && isPrioritizing) {
        setIsPrioritizing(false);
        refetch();
        toast.success("Variants prioritized successfully");
      }
    }
  }, [statusData, isPrioritizing, refetch]);

  React.useEffect(() => {
    if (data?.hpo_terms && data.hpo_terms.length > 0) {
      setSelectedHPOTerms(data.hpo_terms);
    }
  }, [data?.hpo_terms]);

  const [rowCountState, setRowCountState] = React.useState(data?.total || 0);

  React.useEffect(() => {
    if (data && !hasInitialData) {
      setHasInitialData(true);
    }
  }, [data, hasInitialData]);

  React.useEffect(() => {
    setRowCountState((prevRowCountState) =>
      data?.total !== undefined ? data?.total : prevRowCountState
    );
  }, [data?.total]);

  // Memoize handlers
  const handleFilterApply = React.useCallback((newFilterModel) => {
    setFilterModel((prevFilterModel) => {
      const filtersChanged =
        JSON.stringify(prevFilterModel) !== JSON.stringify(newFilterModel);
      if (filtersChanged) {
        setPage(0);
      }
      return newFilterModel;
    });
  }, []);

  const handleClearFilters = React.useCallback(() => {
    const newFilterModelBase = getFilterModelBase(props.project_type);
    setFilterModel((prevFilterModel) => {
      const filtersChanged =
        JSON.stringify(prevFilterModel) !== JSON.stringify(newFilterModelBase);
      if (filtersChanged) {
        setPage(0);
      }
      return newFilterModelBase;
    });
  }, [props.project_type]);

  const handleHPOPrioritize = React.useCallback(async (hpoTerms, sampleId) => {
    try {
      setIsPrioritizing(true);
      setSelectedHPOTerms(hpoTerms);
      await prioritizeVariantsByHPO(hpoTerms, sampleId);
      toast.info("Prioritization started. This may take a few moments...");
    } catch (error) {
      console.error("Failed to prioritize variants:", error);
      setIsPrioritizing(false);
      toast.error(
        error.response?.data?.message || "Failed to prioritize variants"
      );
    }
  }, []);

  const handlePageChange = React.useCallback((newPage) => setPage(newPage), []);
  const handlePageSizeChange = React.useCallback(
    (newPageSize) => setPageSize(newPageSize),
    []
  );

  // Memoize components object
  const gridComponents = React.useMemo(
    () => ({
      Row: VariantRow,
      Header: NullHeader,
      NoRowsOverlay: NoRowsOverlay,
    }),
    []
  );

  // Memoize componentsProps
  const gridComponentsProps = React.useMemo(
    () => ({
      row: {
        project_type: props.project_type,
        bam_files: props.bam_files,
        apiRef: apiRef,
      },
    }),
    [props.project_type, props.bam_files, apiRef]
  );

  // Memoize rows to prevent unnecessary re-renders
  const rows = React.useMemo(() => data?.snvs || [], [data?.snvs]);

  if (isLoading && !hasInitialData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "300px",
        }}
      >
        <img src={preLodaer} alt="preloader" />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "300px",
        }}
      >
        <p>Error loading data: {error.message}</p>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: data?.snvs && data.total === 0 ? "300px" : "1000px",
        width: "100%",
        position: "relative",
      }}
    >
      <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
        <FilterToolbar
          onFilterApply={handleFilterApply}
          onClearFilters={handleClearFilters}
          filterModel={filterModel}
        />
        <HPOPrioritization
          onPrioritize={handleHPOPrioritize}
          projectId={props.project_id}
          sampleId={sampleId}
          isAlreadyPrioritized={isPrioritized}
          isPrioritizing={isPrioritizing}
          selectedHPOTerms={selectedHPOTerms}
        />
      </Box>
      <Box sx={{ display: "flex", height: "calc(100% - 60px)" }}>
        <Box sx={{ flexGrow: 1, position: "relative" }}>
          {isFetching && hasInitialData && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.5)",
                zIndex: 999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={40} sx={{ color: "#1a4774ff" }} />
            </Box>
          )}

          <DataGrid
            rowCount={rowCountState}
            apiRef={apiRef}
            columns={columns}
            pagination
            paginationMode="server"
            rows={rows}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            filterMode="server"
            loading={isFetching}
            components={gridComponents}
            componentsProps={gridComponentsProps}
            disableSelectionOnClick
            sx={{
              border: 0,
              "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader": {
                display: "none",
              },
              "& .MuiDataGrid-virtualScroller": {
                marginTop: "0 !important",
              },
            }}
            disableVirtualization
          />
        </Box>
      </Box>
    </Box>
  );
}

export default VariantList;
